import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    ComposerPrefill,
    NoteEditSubmit,
    NoteEntry,
    NoteKind,
    NoteMention,
    NoteReplySubmit,
    NoteSelection,
    NoteDocumentAnchorSelect,
    NoteSubmit,
    NoteTag,
    NoteTextSegment,
    NoteUser,
} from './account-notes.types';
import { mentionSegments } from '../mention-input/mention-text';

//REV-21365 — Kommentare am Konto (Pure View). Kein Store, kein HTTP, keine Sortierung, keine
//Formatierung von Datum oder Betrag. Entwurf: ADR-0019.

/** Welche Marken zu welcher Art gehören — und welche vorausgewählt ist. */
const TAGS_BY_KIND: Record<NoteKind, NoteTag[]> = {
    COMMENT: ['internal', 'closingMeeting'],
    TODO: ['todo', 'reviewer', 'client'],
};
const DEFAULT_TAG: Record<NoteKind, NoteTag> = { COMMENT: 'internal', TODO: 'todo' };

const MENU_GAP = 2;
/** Room the two-item menu needs below its button before it opens upwards instead. */
const MENU_HEIGHT = 80;

type MenuPosition = { top: number; right: number; isAbove: boolean };

/** Right-aligned under the dots, or above them near the window's bottom edge. */
function menuPositionAt(rect: DOMRect): MenuPosition {
    const right = window.innerWidth - rect.right;
    if (rect.bottom + MENU_GAP + MENU_HEIGHT > window.innerHeight) {
        // `translateY(-100%)` in the template lifts the menu by its own height.
        return { top: rect.top - MENU_GAP, right, isAbove: true };
    }
    return { top: rect.bottom + MENU_GAP, right, isAbove: false };
}

@Component({
    selector: 'abcp-account-notes',
    templateUrl: './account-notes.component.html',
    styleUrls: ['./account-notes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountNotesComponent implements OnChanges, AfterViewChecked {
    /**
     * Die Kommentare DIESES Kontos, **älteste zuoberst** — wie ein Nachrichtenverlauf (AC1).
     *
     * ⚠️ Die Ansicht sortiert nicht. Sie zeigt, was sie bekommt, in der Reihenfolge, in der sie es
     * bekommt; die Reihenfolge ist eine Zusage des Hosts.
     */
    @Input() entries: NoteEntry[] = [];

    /** Zahl im Kopf. Kommt herein, wird nie aus `entries` gezählt — die Liste kann blättern. */
    @Input() totalCount = 0;

    @Input() accountLabel = '';
    @Input() loading = false;
    @Input() error = false;

    /** Personen für die `@`-Vorschlagsliste. Ein Pure View lädt keine Benutzer. */
    @Input() users: NoteUser[] = [];

    /** Wer gerade angemeldet ist — steht nicht in der eigenen Vorschlagsliste (AC6). */
    @Input() currentUserId: string | null = null;

    /** Eine markierte Textstelle aus dem Kontenblatt; `null` ⇒ kein Zitat-Kästchen (AC11). */
    @Input() selection: NoteSelection | null = null;

    /**
     * Vorbelegung des Verfassers; **`null` = zu** — dieselbe Form wie `manualEntry` und
     * `reviewDialog` am `abcp-account-workspace`.
     *
     * ⚠️ Was danach getippt wird, gehört der Ansicht. Ein Entwurf ist kein Abschlusszustand: ihn
     * über den Host zu führen hiesse, für jeden Tastendruck eine Antwort zu verlangen.
     */
    @Input() composer: ComposerPrefill | null = null;
    /** The notes quoting this block (e.g. a Kontenblatt line) are highlighted and scrolled into view. */
    @Input() activeBlockId: string | null = null;

    /** Ein Eintrag im Bearbeiten-Modus; `null` ⇒ keiner. Kontrolliert, nie selbst gesetzt. */
    @Input() editingNoteId: string | null = null;

    /** Aufgeklappte Threads. Kontrolliert — der Host entscheidet, was offen ist. */
    @Input() expandedNoteIds: string[] = [];

    @Output() noteSubmit = new EventEmitter<NoteSubmit>();
    @Output() noteEditStart = new EventEmitter<string>();
    @Output() noteEditSubmit = new EventEmitter<NoteEditSubmit>();
    @Output() noteEditCancel = new EventEmitter<void>();
    @Output() noteDelete = new EventEmitter<string>();
    @Output() replySubmit = new EventEmitter<NoteReplySubmit>();
    @Output() threadToggle = new EventEmitter<string>();
    @Output() anchorSelect = new EventEmitter<string>();
    /** REV-21369 (N4) — Zitat einer Markierung im Beleg geklickt. Wohin das führt, entscheidet der Host. */
    @Output() documentAnchorSelect = new EventEmitter<NoteDocumentAnchorSelect>();
    /** REV-22352 — the quote checkbox changed; the host shows the marking in the document only while it is ticked. */
    @Output() anchorAttachChange = new EventEmitter<boolean>();
    /** REV-22352 — the composer was closed without sending, so the host drops the marking it holds. */
    @Output() composerCancel = new EventEmitter<void>();

    // ── Entwurf des Verfassers: ansichts-lokal, siehe `composer` oben ──────────────────────────
    composerOpen = false;
    composerText = '';
    composerTags: NoteTag[] = ['internal'];
    composerAttachAnchor = true;
    /** Picked in the field; only these are notified. */
    composerMentions: NoteMention[] = [];

    /** Welches Aktionen-Menü offen ist, und wo die Löschen-Rückfrage steht. */
    openMenuNoteId: string | null = null;
    menuPosition: MenuPosition | null = null;
    confirmDeleteNoteId: string | null = null;

    /** Entwurf der Bearbeitung — gefüllt aus dem Eintrag, sobald er in den Modus kommt. */
    editText = '';
    editTags: NoteTag[] = [];
    editMentions: NoteMention[] = [];

    replyDrafts: Record<string, string> = {};
    replyMentions: Record<string, NoteMention[]> = {};

    /**
     * Die Liste selbst — gebraucht, um sie ans ENDE zu rollen.
     *
     * ⚠️ Das ist die einzige Stelle, an der diese Ansicht das DOM anfasst, und sie tut es
     * nur lesend-und-rollend: kein Zustand, keine Entscheidung. Eine Bildlaufposition ist
     * nichts, was ein Wirt hereinreichen kann — sie hängt an der gerenderten Höhe.
     */
    @ViewChild('list') private listRef?: ElementRef<HTMLElement>;

    /**
     * ⚠️ NUR das erste Mal, und das ist der ganze Trick.
     *
     * Der Bereich liest sich wie ein Gespräch: älteste Beiträge oben, der neueste unten. Beim
     * Öffnen stand man deshalb am ANFANG und musste erst nach unten rollen, um zu sehen, was
     * zuletzt gesagt wurde — genau andersherum als in jedem Chat.
     *
     * Bei JEDEM Zeichnen zu rollen wäre die falsche Antwort: wer nach oben gescrollt hat, um
     * etwas nachzulesen, würde bei der nächsten Änderung wieder ans Ende gerissen.
     */
    private scrolledToLatest = false;

    /** Set until a highlighted note is rendered; runs after the scroll to the latest note. */
    private revealPending = false;

    ngAfterViewChecked(): void {
        this.scrollToLatestOnce();
        this.revealActive();
    }

    private scrollToLatestOnce(): void {
        if (this.scrolledToLatest || this.loading || this.entries.length === 0) {
            return;
        }
        const list = this.listRef?.nativeElement;
        if (!list) {
            return;
        }
        list.scrollTop = list.scrollHeight;
        this.scrolledToLatest = true;
    }

    private revealActive(): void {
        if (!this.revealPending || this.activeBlockId === null) {
            return;
        }
        const note = this.listRef?.nativeElement.querySelector('[data-active="true"]');
        if (note) {
            note.scrollIntoView({ block: 'nearest' });
            this.revealPending = false;
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        // ⚠️ Ein anderes Konto ist ein anderes Gespraech — der Blick geht wieder ans Ende.
        // Ohne das behielte der Bereich die Bildlaufposition des vorigen Kontos.
        if (changes['accountLabel'] && !changes['accountLabel'].firstChange) {
            this.scrolledToLatest = false;
        }

        if (changes['composer']) {
            this.applyComposerPrefill();
        }
        if (changes['activeBlockId']) {
            this.revealPending = this.activeBlockId !== null;
        }
        if (changes['editingNoteId'] || changes['entries']) {
            this.seedEditDraft();
        }
    }

    // ── Kopf und Zustände ─────────────────────────────────────────────────────────────────────

    get isEmpty(): boolean {
        return this.entries.length === 0;
    }

    readonly skeletonRows = [1, 2, 3];

    // ── Der einzelne Eintrag ──────────────────────────────────────────────────────────────────

    trackById(_index: number, entry: NoteEntry): string {
        return entry.id;
    }

    /**
     * Marken eines Eintrags — **genau `internal` bleibt unbeschriftet** (AC2).
     *
     * Sonst trüge jede Zeile dieselbe Marke und keine sagte mehr etwas.
     */
    visibleTags(entry: NoteEntry): NoteTag[] {
        const onlyInternal = entry.tags.length === 1 && entry.tags[0] === 'internal';
        return onlyInternal ? [] : entry.tags;
    }

    /** Zerlegt den Text in Fliesstext und Erwähnungen. Die Ansicht parst KEINE Speicher-Token. */
    segments(text: string, mentions: { displayName: string }[]): NoteTextSegment[] {
        return mentionSegments(text, mentions);
    }

    isActive(entry: NoteEntry): boolean {
        return this.activeBlockId !== null && entry.anchor?.blockId === this.activeBlockId;
    }

    isExpanded(entry: NoteEntry): boolean {
        return this.expandedNoteIds.indexOf(entry.id) !== -1;
    }

    onAnchorClick(entry: NoteEntry): void {
        const blockId = entry.anchor?.blockId;
        const documentId = entry.anchor?.documentId;
        if (blockId) {
            this.anchorSelect.emit(blockId);
        } else if (documentId) {
            this.documentAnchorSelect.emit({ noteId: entry.id, documentId });
        }
    }

    // ── Aktionen-Menü, Löschen ────────────────────────────────────────────────────────────────

    toggleMenu(entry: NoteEntry, trigger: HTMLElement): void {
        const opening = this.openMenuNoteId !== entry.id;
        this.openMenuNoteId = opening ? entry.id : null;
        this.menuPosition = opening ? menuPositionAt(trigger.getBoundingClientRect()) : null;
        this.confirmDeleteNoteId = null;
    }

    closeMenu(): void {
        this.openMenuNoteId = null;
        this.menuPosition = null;
    }

    askDelete(entry: NoteEntry): void {
        this.confirmDeleteNoteId = entry.id;
        this.closeMenu();
    }

    confirmDelete(entry: NoteEntry): void {
        this.confirmDeleteNoteId = null;
        this.noteDelete.emit(entry.id);
    }

    cancelDelete(): void {
        this.confirmDeleteNoteId = null;
    }

    // ── Bearbeiten ────────────────────────────────────────────────────────────────────────────

    startEdit(entry: NoteEntry): void {
        this.closeMenu();
        this.noteEditStart.emit(entry.id);
    }

    isEditing(entry: NoteEntry): boolean {
        return this.editingNoteId === entry.id;
    }

    toggleEditTag(tag: NoteTag): void {
        this.editTags = toggleKeepingOne(this.editTags, tag);
    }

    isEditTagOn(tag: NoteTag): boolean {
        return this.editTags.indexOf(tag) !== -1;
    }

    tagsFor(kind: NoteKind): NoteTag[] {
        return TAGS_BY_KIND[kind];
    }

    saveEdit(entry: NoteEntry): void {
        /** ⚠️ Kontrolliert: gemeldet wird der Entwurf, angezeigt bleibt der hereingegebene Wert. */
        this.noteEditSubmit.emit({
            id: entry.id,
            text: this.editText.trim(),
            tags: this.editTags,
            mentions: this.editMentions,
        });
    }

    cancelEdit(): void {
        this.seedEditDraft();
        this.noteEditCancel.emit();
    }

    // ── Antworten ─────────────────────────────────────────────────────────────────────────────

    replyDraft(entry: NoteEntry): string {
        return this.replyDrafts[entry.id] ?? '';
    }

    setReplyDraft(entry: NoteEntry, value: string): void {
        this.replyDrafts = { ...this.replyDrafts, [entry.id]: value };
    }

    replyMentionsOf(entry: NoteEntry): NoteMention[] {
        return this.replyMentions[entry.id] ?? [];
    }

    setReplyMentions(entry: NoteEntry, mentions: NoteMention[]): void {
        this.replyMentions = { ...this.replyMentions, [entry.id]: mentions };
    }

    sendReply(entry: NoteEntry): void {
        const text = this.replyDraft(entry).trim();
        if (!text) {
            return;
        }
        this.replySubmit.emit({ noteId: entry.id, text, mentions: this.replyMentionsOf(entry) });
        this.setReplyDraft(entry, '');
        this.setReplyMentions(entry, []);
    }

    // ── Der Verfasser ─────────────────────────────────────────────────────────────────────────

    openComposer(): void {
        this.composerOpen = true;
    }

    cancelComposer(): void {
        this.composerOpen = false;
        this.resetDraft();
        this.composerCancel.emit();
    }

    setAttachAnchor(attach: boolean): void {
        this.composerAttachAnchor = attach;
        this.anchorAttachChange.emit(attach);
    }

    toggleComposerTag(tag: NoteTag): void {
        this.composerTags = toggleKeepingOne(this.composerTags, tag);
    }

    isComposerTagOn(tag: NoteTag): boolean {
        return this.composerTags.indexOf(tag) !== -1;
    }

    get canSend(): boolean {
        return this.composerText.trim().length > 0;
    }

    send(): void {
        if (!this.canSend) {
            return;
        }
        const attach = this.selection && this.composerAttachAnchor;
        this.noteSubmit.emit({
            kind: 'COMMENT',
            text: this.composerText.trim(),
            tags: this.composerTags,
            mentionUserIds: this.composerMentions.map((m) => m.userId),
            anchor: attach ? { snippet: this.selection!.snippet, blockId: this.selection!.blockId ?? null } : null,
        });
        this.composerOpen = this.composer !== null;
        this.resetDraft();
    }

    // ── privat ────────────────────────────────────────────────────────────────────────────────

    private applyComposerPrefill(): void {
        this.composerOpen = this.composer !== null;
        this.resetDraft();
    }

    /** Comments only: a Pendenz is created in the account's Pendenzen area, never here. */
    private resetDraft(): void {
        this.composerText = '';
        this.composerTags = [DEFAULT_TAG.COMMENT];
        this.composerMentions = [];
        this.composerAttachAnchor = true;
    }

    private seedEditDraft(): void {
        const entry = this.entries.find((e) => e.id === this.editingNoteId);
        this.editText = entry?.text ?? '';
        this.editTags = entry ? [...entry.tags] : [];
        this.editMentions = entry ? [...entry.mentions] : [];
    }
}

/** Die letzte Marke lässt sich nicht abwählen — mindestens eine ist Pflicht (AC4). */
function toggleKeepingOne(tags: NoteTag[], tag: NoteTag): NoteTag[] {
    const on = tags.indexOf(tag) !== -1;
    if (!on) {
        return [...tags, tag];
    }
    return tags.length > 1 ? tags.filter((t) => t !== tag) : tags;
}
